import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CCY_CODES,
  D,
  MONEY_SCALE,
  calcProfit,
  fmtAmt,
  type CcyCode,
  type EditProvodkaInput,
  type Money,
  type OrderDetail,
  type Provodka,
  type ReverseEntry,
} from '@wave/shared';
import { useExecutorHistory, useOrder } from '../../api/hooks';
import { useAuthStore, hasRight } from '../../auth/store';
import { useConfirmProvodka, useEditProvodka, useRollbackConfirm, useUpdateProfitRates } from '../../api/mutations';
import { FLOW_STEP_TO_PROVODKA_IDX, FlowSteps } from '../FlowSteps';
import { FormulaHint } from '../FormulaHint';
import {
  bdMargin,
  bdProfit,
  bdUsdtEquivalent,
  buildProfitChecks,
} from '../../lib/breakdown';
import { Modal } from '../Modal';
import { Combobox } from '../Combobox';
import { StatusBadge } from '../StatusBadge';
import { ProgressBar } from '../ProgressBar';
import { RejectModal } from './RejectModal';
import { DeleteModal } from './DeleteModal';
import { ChangeExecutorModal } from './ChangeExecutorModal';
import { ClientMessageModal } from './ClientMessageModal';
import { naturalRate } from '../../lib/rate';
import {
  btnGhostSm,
  btnPrimary,
  btnSecondary,
  btnSecondarySm,
  btnSuccessSm,
  inputCls,
} from '../../lib/styles';

const MONTHS = [
  'янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

function formatBusinessDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const day = Number(m[3]);
  const month = MONTHS[Number(m[2]) - 1];
  return `${day} ${month} ${m[1]}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getDate();
  const month = MONTHS[d.getMonth()];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${hh}:${mm}`;
}

export function OrderDetailModal() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading, error, refetch } = useOrder(id ?? null);
  const rights = useAuthStore((s) => s.rights);
  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingExecutor, setChangingExecutor] = useState(false);
  const [showClientMsg, setShowClientMsg] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [showJournal, setShowJournal] = useState(false);

  function close() {
    navigate(-1);
  }

  const canConfirm = hasRight(rights, 'orders.confirm');
  const canReject = hasRight(rights, 'orders.reject');
  const canEdit = hasRight(rights, 'orders.edit-provodka');
  const canDelete = hasRight(rights, 'orders.delete');
  const canRollback = hasRight(rights, 'orders.rollback-confirm');
  const canChangeExecutor = hasRight(rights, 'orders.change-executor');

  // Смена исполнителя доступна, только пока обе ноги исполнителя (idx2/idx3) не
  // подтверждены — подтверждённая нога = деньги реально прошли через прежнего.
  const executorLegsPending =
    !!order &&
    order.statusFlag === 'active' &&
    order.provodki.filter((p) => p.idx >= 2).every((p) => p.status === 'pending');
  const showChangeExecutor = canChangeExecutor && executorLegsPending;

  return (
    <>
      <Modal title={`Заявка ${id}`} onClose={close} size="xl">
        {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {(error as Error).message}
          </div>
        )}
        {order && (
          <div className="space-y-6">
            <MetaStrip
              order={order}
              canReject={canReject}
              onReject={() => setRejecting(true)}
              onClientMessage={() => setShowClientMsg(true)}
            />
            <DealCard
              order={order}
              canEditRates={canEdit && order.status !== 'closed' && order.status !== 'reject' && order.status !== 'deleted'}
              executorAction={
                showChangeExecutor ? (
                  <button
                    type="button"
                    onClick={() => setChangingExecutor(true)}
                    className="mt-2 w-full rounded-md border border-border bg-bg px-2.5 py-1 text-xs font-medium text-text2 transition-colors duration-150 hover:bg-bg2 hover:text-text"
                  >
                    Сменить исполнителя
                  </button>
                ) : null
              }
            />
            <ExecutorHistory orderId={order.id} />
            <Legs
              order={order}
              canConfirm={canConfirm && order.status !== 'deleted'}
              canEdit={canEdit && order.status !== 'deleted'}
              canRollback={canRollback && order.statusFlag === 'active'}
              editingIdx={editingIdx}
              setEditingIdx={setEditingIdx}
              onChanged={() => void refetch()}
            />
            <JournalToggle
              order={order}
              expanded={showJournal}
              onToggle={() => setShowJournal((v) => !v)}
            />
            <ContextActions order={order} onClose={close} canDelete={canDelete} onDelete={() => setDeleting(true)} />
          </div>
        )}
      </Modal>

      {rejecting && order && (
        <RejectModal
          orderId={order.id}
          onClose={() => setRejecting(false)}
          onDone={() => {
            setRejecting(false);
            void refetch();
          }}
        />
      )}

      {deleting && order && (
        <DeleteModal
          orderId={order.id}
          onClose={() => setDeleting(false)}
          onDone={() => {
            setDeleting(false);
            void refetch();
          }}
        />
      )}

      {changingExecutor && order && (
        <ChangeExecutorModal
          orderId={order.id}
          currentExecutor={order.executor}
          onClose={() => setChangingExecutor(false)}
          onDone={() => {
            setChangingExecutor(false);
            void refetch();
          }}
        />
      )}

      {showClientMsg && order && (
        <ClientMessageModal order={order} onClose={() => setShowClientMsg(false)} />
      )}
    </>
  );
}

// ============================================================================
// История смены исполнителя — «почему сменился» (из order_audit)
// ============================================================================

function ExecutorHistory({ orderId }: { orderId: string }) {
  const { data } = useExecutorHistory(orderId);
  if (!data || data.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-bg2 p-4">
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-text2">
        Смена исполнителя
      </h3>
      <ul className="space-y-2.5">
        {data.map((e) => (
          <li key={e.id} className="border-l-2 border-border-strong pl-3 text-sm">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-mono text-text2">{e.from}</span>
              <span className="text-text-subtle">→</span>
              <span className="font-mono font-medium text-accent">{e.to}</span>
              <span className="ml-1 text-[11px] text-text-subtle">
                {formatDateTime(e.at)}
                {e.by ? ` · ${e.by}` : ''}
              </span>
            </div>
            <div className="mt-0.5 text-text2">{e.reason}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// 1. Meta strip — компактный ряд мета-данных под заголовком модалки
// ============================================================================

function MetaStrip({
  order,
  canReject,
  onReject,
  onClientMessage,
}: {
  order: OrderDetail;
  canReject: boolean;
  onReject: () => void;
  onClientMessage: () => void;
}) {
  const showReject = order.statusFlag === 'active' && order.progress === 0 && canReject;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-bg2 px-4 py-3 text-sm">
      <StatusBadge status={order.status} />
      {order.status !== 'reject' && (
        <div className="flex items-center gap-2">
          <ProgressBar done={order.progress} />
        </div>
      )}
      <Divider />
      <div className="flex items-center gap-1.5">
        <span className="text-text2">Дата</span>
        <span className="font-mono font-medium text-text">{formatBusinessDate(order.date)}</span>
      </div>
      <Divider />
      <div className="flex items-center gap-1.5">
        <span className="text-text2">Оператор</span>
        <span className="font-mono font-medium text-text">{order.operator}</span>
      </div>
      <Divider />
      <div className="flex items-center gap-1.5 text-text2">
        <span>Создана</span>
        <span className="font-mono">{formatDateTime(order.createdAt)}</span>
      </div>
      {order.updatedAt !== order.createdAt && (
        <>
          <Divider />
          <div className="flex items-center gap-1.5 text-text2">
            <span>Обновлена</span>
            <span className="font-mono">{formatDateTime(order.updatedAt)}</span>
          </div>
        </>
      )}
      <div className="ml-auto flex items-center gap-2">
        <button type="button" onClick={onClientMessage} className={btnSecondarySm}>
          Сообщение клиенту
        </button>
        {showReject && (
          <button
            type="button"
            onClick={onReject}
            className="rounded-md border border-[var(--red-border)] bg-bg px-2.5 py-1 text-xs font-medium text-danger transition-colors duration-150 hover:bg-[var(--red-bg)]"
          >
            Отклонить заявку
          </button>
        )}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-3 w-px bg-border" />;
}

// ============================================================================
// 2. Deal card — flow клиент ↔ мы ↔ исполнитель + спред + профит
// ============================================================================

function DealCard({
  order,
  canEditRates,
  executorAction,
}: {
  order: OrderDetail;
  canEditRates: boolean;
  executorAction?: React.ReactNode;
}) {
  const isReject = order.status === 'reject';
  // Валюты конвертации профита: участвуют в формуле (clGive/exGet), кроме USDT
  // и кроме same-ccy сделок (профит нативный).
  const profitFixCcys =
    order.clGiveCcy === order.exGetCcy
      ? []
      : [...new Set([order.clGiveCcy, order.exGetCcy])].filter((c) => c !== 'USDT');
  // Выполненность шагов диаграммы: шаг потока → статус соответствующей проводки.
  const doneSteps = Object.fromEntries(
    Object.entries(FLOW_STEP_TO_PROVODKA_IDX).map(([step, idx]) => [
      step,
      order.provodki.find((p) => p.idx === idx)?.status === 'done',
    ]),
  );

  // USDT-эквиваленты считаются по snapshot курсов на момент создания заявки —
  // иммутабельны, гарантируют воспроизводимость профита независимо от
  // последующих изменений курсов в /admin/rates.
  const snap = order.usdtRatesSnapshot;
  const giveRate = snap[order.clGiveCcy];
  const payRate = snap[order.exGetCcy];
  const isLoss = D(order.profitAmount).isNegative();
  const profitUsdt = D(order.profitAmount);
  // «Объём» — реально полученное от клиента (с коэффициентом). Маржа же
  // считается от БАЗЫ — той части, что участвует в профите (дельта — транзит).
  const receiveUsdt = giveRate ? D(order.clGiveAmt).dividedBy(D(giveRate)) : null;
  const receiveUsdtBase = giveRate
    ? D(order.clGiveBaseAmt ?? order.clGiveAmt).dividedBy(D(giveRate))
    : null;
  // Дельта коэффициента в USDT — БЕЗ фикса. Считаем из нативной дельты минус
  // fixClAmt (как в строке коэффициента), иначе «коэфф транзитом» завышен на
  // фикс; точно 0 когда коэффициента нет (без остатка округления).
  const coefUsdt = giveRate
    ? D(order.clGiveAmt)
        .minus(D(order.clGiveBaseAmt ?? order.clGiveAmt))
        .minus(D(order.fixClAmt ?? '0'))
        .dividedBy(D(giveRate))
    : null;
  const payUsdt = payRate ? D(order.exGetAmt).dividedBy(D(payRate)) : null;
  const profitPct =
    receiveUsdtBase && receiveUsdtBase.gt(0)
      ? profitUsdt.dividedBy(receiveUsdtBase).times(100)
      : null;

  // ─── Разборы «как получилась цифра» (FormulaHint) ───
  // ВАЖНО: новое поле, влияющее на суммы/профит, обязано попасть и сюда —
  // в термы соответствующих разборов (см. lib/breakdown.ts и CLAUDE.md).
  const profitHint = useMemo(() => {
    if (isReject || order.status === 'deleted') return null;
    try {
      // Обмен исполнителя без «надбавок» ноги idx2 — как в серверном calcProfit.
      // Курьер влит в idx2 только в режиме 'extra' (в 'included' удержан из выдачи).
      const exchange = D(order.exGetAmt)
        .minus(D(order.fixExAmt ?? '0'))
        .minus(D(order.bonusExAmt ?? '0'))
        .minus(D(order.courierMode === 'extra' ? (order.courierExAmt ?? '0') : '0'))
        .toString();
      const input = {
        clGiveCcy: order.clGiveCcy,
        clGiveAmt: order.clGiveBaseAmt ?? order.clGiveAmt,
        clGetCcy: order.clGetCcy,
        clGetAmt: order.clGetAmt,
        exGiveCcy: order.exGiveCcy,
        exGiveAmt: order.exGiveAmt,
        exGetCcy: order.exGetCcy,
        exGetAmt: exchange,
        rates: snap,
        bonusUsdt: order.bonusUsdt ?? undefined,
      };
      // Гвард честности: если дерево расходится с сохранённым профитом
      // (legacy-заявки до текущей модели / неполный снапшот) — разбор скрываем.
      const computed = calcProfit(input);
      if (D(computed.amount).minus(D(order.profitAmount)).abs().gt('0.05')) return null;
      return bdProfit({
        input,
        ratesBadge: 'снапшот',
        checks: buildProfitChecks({
          profitUsdt: order.profitAmount,
          marginPct: profitPct ? profitPct.toDecimalPlaces(4).toString() : null,
          rateChecks: [],
        }),
      });
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, snap, isReject]);

  const volumeHint = useMemo(() => {
    if (!giveRate || isReject) return null;
    return bdUsdtEquivalent({
      kind: 'volume',
      amt: order.clGiveAmt,
      ccy: order.clGiveCcy,
      rate: giveRate,
      ratesBadge: 'снапшот',
      note:
        order.clGiveBaseAmt && order.clGiveBaseAmt !== order.clGiveAmt
          ? 'Полная сумма клиента (с коэффициентом/фиксом); профит считается от базы.'
          : undefined,
    });
  }, [order, giveRate, isReject]);

  const expenseHint = useMemo(() => {
    if (!payRate || isReject) return null;
    return bdUsdtEquivalent({
      kind: 'expense',
      amt: order.exGetAmt,
      ccy: order.exGetCcy,
      rate: payRate,
      ratesBadge: 'снапшот',
      note:
        order.fixExAmt || order.bonusExAmt || (order.courierMode === 'extra' && order.courierExAmt)
          ? 'Полная оплата исполнителю (с фиксом/бонусом/курьером-«сверх»); в профите обмен без надбавок.'
          : undefined,
    });
  }, [order, payRate, isReject]);

  const marginHint = useMemo(() => {
    if (!receiveUsdtBase || !receiveUsdtBase.gt(0) || isReject) return null;
    return bdMargin({
      profitUsdt: order.profitAmount,
      baseUsdt: receiveUsdtBase.toDecimalPlaces(4).toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.profitAmount, receiveUsdtBase?.toString(), isReject]);

  return (
    <div className="rounded-lg border border-border bg-bg2 p-5">
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[1fr_auto_1fr]">
        {/* Клиент */}
        <Side
          title="Клиент"
          account={order.client}
          giveLabel="отдаёт нам"
          giveAmt={order.clGiveAmt}
          giveCcy={order.clGiveCcy}
          getLabel="получает"
          getAmt={order.clGetAmt}
          getCcy={order.clGetCcy}
          ratePair={naturalRate(order.clGiveAmt, order.clGiveCcy, order.clGetAmt, order.clGetCcy)}
        />

        {/* Центральный flow — стрелка-разделитель */}
        <FlowArrow />

        {/* Исполнитель */}
        <Side
          title="Исполнитель"
          account={order.executor}
          giveLabel="отдаёт нам"
          giveAmt={order.exGiveAmt}
          giveCcy={order.exGiveCcy}
          getLabel="берёт у нас"
          getAmt={order.exGetAmt}
          getCcy={order.exGetCcy}
          ratePair={naturalRate(order.exGetAmt, order.exGetCcy, order.exGiveAmt, order.exGiveCcy)}
          action={executorAction}
        />
      </div>

      {/* Коэффициент: наценка на сумму клиента (транзит рефереру, в профит не входит).
          Из-за неё clGiveAmt × clRate ≠ clGetAmt — поясняем расклад оператору. */}
      {order.coefPct && order.clGiveBaseAmt && !D(order.coefPct).isZero() && (
        <div className="mt-4 rounded-md border border-border-subtle bg-bg p-3 text-[12px] text-text2">
          <span className="font-semibold uppercase tracking-wide text-[10px]">Коэффициент:</span>{' '}
          <span className="font-mono tabular-nums text-text">
            {D(order.coefPct).gt(0) ? '+' : ''}
            {D(order.coefPct).toString()}%
          </span>{' '}
          ={' '}
          <span className="font-mono tabular-nums text-text">
            {fmtAmt(
              D(order.clGiveAmt)
                .minus(D(order.clGiveBaseAmt))
                .minus(D(order.fixClAmt ?? '0'))
                .toString(),
              order.clGiveCcy,
            )}
          </span>{' '}
          в сумме клиента (база {fmtAmt(order.clGiveBaseAmt, order.clGiveCcy)} ×{' '}
          {D(1).plus(D(order.coefPct).dividedBy(100)).toString()}). Профит считается от базы.
        </div>
      )}

      {/* Фикс за платёжку (транзитом): берём с клиента и отдаём исполнителю 1:1,
          в профит не входит. Влит в ноги idx0/idx2, новых проводок нет. */}
      {order.fixUsdt && !D(order.fixUsdt).isZero() && (
        <div className="mt-4 rounded-md border border-border-subtle bg-bg p-3 text-[12px] text-text2">
          <span className="font-semibold uppercase tracking-wide text-[10px]">Фикс за платёжку:</span>{' '}
          <span className="font-mono tabular-nums text-text">{fmtAmt(order.fixUsdt, 'USDT')} USDT</span>{' '}
          — клиент +
          <span className="font-mono tabular-nums text-text">
            {fmtAmt(order.fixClAmt ?? '0', order.clGiveCcy)}
          </span>
          , исполнитель +
          <span className="font-mono tabular-nums text-text">
            {fmtAmt(order.fixExAmt ?? '0', order.exGetCcy)}
          </span>
          . Транзитом 1:1, профит не меняется.
        </div>
      )}

      {/* Бонус исполнителю (благодарность): влит в ногу idx2, вычтен из профита.
          В отличие от фикса — оплачен из маржи компании, не 1:1 с клиента. */}
      {order.bonusUsdt && !D(order.bonusUsdt).isZero() && (
        <div className="mt-4 rounded-md border border-border-subtle bg-bg p-3 text-[12px] text-text2">
          <span className="font-semibold uppercase tracking-wide text-[10px]">Бонус исполнителю:</span>{' '}
          <span className="font-mono tabular-nums text-text">{fmtAmt(order.bonusUsdt, 'USDT')} USDT</span>{' '}
          — исполнитель +
          <span className="font-mono tabular-nums text-text">
            {fmtAmt(order.bonusExAmt ?? '0', order.exGetCcy)}
          </span>
          . Благодарность за сделку, оплачена из профита (нетто = спред − бонус).
        </div>
      )}

      {/* Курьер: целиком исполнителю. extra — доплатил клиент (влит в idx0/idx2);
          included — удержан из выдачи клиенту (idx1/idx3 уменьшены, idx2 не рос). */}
      {order.courierUsdt && !D(order.courierUsdt).isZero() && (
        <div className="mt-4 rounded-md border border-border-subtle bg-bg p-3 text-[12px] text-text2">
          <span className="font-semibold uppercase tracking-wide text-[10px]">Курьер:</span>{' '}
          <span className="font-mono tabular-nums text-text">{fmtAmt(order.courierUsdt, 'USDT')} USDT</span>{' '}
          {order.courierMode === 'extra' ? (
            <>
              — «сверх»: клиент доплатил +
              <span className="font-mono tabular-nums text-text">
                {fmtAmt(order.courierClAmt ?? '0', order.clGiveCcy)}
              </span>
              , исполнителю +
              <span className="font-mono tabular-nums text-text">
                {fmtAmt(order.courierExAmt ?? '0', order.exGetCcy)}
              </span>
              . Транзитом, профит не меняется.
            </>
          ) : (
            <>
              — «из суммы»: клиент получил на{' '}
              <span className="font-mono tabular-nums text-text">
                {fmtAmt(order.courierClAmt ?? '0', order.clGetCcy)}
              </span>{' '}
              меньше — разницу исполнитель удержал из выдачи. Профит не меняется.
            </>
          )}
        </div>
      )}

      {/* Курсы сделки: пары сторон + конвертация профита (фикс редактируем) */}
      {!isReject && profitFixCcys.length > 0 && (
        <div className="mt-4 rounded-md border border-border-subtle bg-bg p-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text2">
            {profitFixCcys.length === 1
              ? `Курс конвертации ${profitFixCcys[0]} → USDT`
              : 'Курсы конвертации в USDT'}
          </div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-1.5 font-mono text-[12px] tabular-nums">
            {profitFixCcys.map((ccy) => (
              <span key={ccy} className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="text-text2">1 USDT =</span>
                <RateFix orderId={order.id} ccy={ccy} value={snap[ccy] ?? '—'} editable={canEditRates} />
                <span className="text-text2">{ccy}</span>
              </span>
            ))}
            <span className="text-[10px] font-sans text-text-subtle">
              {canEditRates ? 'клик по курсу — уточнить фактический фикс (попадёт в аудит)' : 'снапшот заявки'}
            </span>
          </div>
        </div>
      )}

      {/* Профит-полоса */}
      <div className="mt-5 border-t border-border-subtle pt-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text2">
              {isReject ? 'Профит' : 'Чистый профит'}
              {profitHint && <FormulaHint formula={profitHint} />}
            </div>
            <div className="mt-1 flex items-baseline gap-3">
              {isReject ? (
                <span className="font-mono text-2xl font-bold text-text2">—</span>
              ) : (
                <>
                  <span
                    className={`font-mono text-2xl font-bold tabular-nums tracking-tight ${
                      isLoss ? 'text-danger' : 'text-success'
                    }`}
                  >
                    {profitUsdt.gte(0) ? '+' : ''}
                    {profitUsdt.toFixed(2)} USDT
                  </span>
                  {order.profitNativeAmount &&
                    order.profitNativeCcy &&
                    !D(order.profitNativeAmount).isZero() && (
                      <span className="font-mono text-sm font-medium tabular-nums text-text2">
                        ≈ {D(order.profitNativeAmount).gte(0) ? '+' : ''}
                        {fmtAmt(order.profitNativeAmount, order.profitNativeCcy)}
                      </span>
                    )}
                </>
              )}
            </div>
          </div>
          {!isReject && (
            <div className="flex items-end gap-4 text-right">
              {receiveUsdt && receiveUsdt.gt(0) && (
                <Stat label="Объём" hint={volumeHint && <FormulaHint formula={volumeHint} />}>
                  <span className="font-mono text-sm font-semibold tabular-nums text-text">
                    {receiveUsdt.toFixed(2)} USDT
                  </span>
                </Stat>
              )}
              {payUsdt && payUsdt.gt(0) && (
                <Stat label="Расход" hint={expenseHint && <FormulaHint formula={expenseHint} />}>
                  <span className="font-mono text-sm font-semibold tabular-nums text-text2">
                    {payUsdt.toFixed(2)} USDT
                  </span>
                </Stat>
              )}
              {profitPct && (
                <Stat label="Маржа" hint={marginHint && <FormulaHint formula={marginHint} />}>
                  <span
                    className={`font-mono text-sm font-semibold tabular-nums ${
                      profitPct.isNegative() ? 'text-danger' : 'text-success'
                    }`}
                  >
                    {profitPct.gte(0) ? '+' : ''}
                    {profitPct.toFixed(2)}%
                  </span>
                </Stat>
              )}
            </div>
          )}
        </div>
        {isReject && order.rejectReason && (
          <div className="mt-3 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            <span className="font-medium">Причина реджекта:</span> {order.rejectReason}
          </div>
        )}
        {order.status === 'deleted' && order.deleteReason && (
          <div className="mt-3 rounded-md border border-border-strong bg-bg3 px-3 py-2 text-sm text-text2">
            <span className="font-medium">Причина удаления:</span> {order.deleteReason}
          </div>
        )}

        {/* Расчёт через USDT — snapshot курсов на момент создания. Скрыто для reject. */}
        {!isReject && order.status !== 'deleted' && receiveUsdt && payUsdt && giveRate && payRate && (
          <div className="mt-4 rounded-md border border-border-subtle bg-bg p-3">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-text2">
                Поток денег
              </span>
              <span className="text-[10px] text-text-subtle">
                залитый номер шага — проводка подтверждена
              </span>
            </div>
            <FlowSteps
              clGiveAmt={order.clGiveAmt}
              clGiveCcy={order.clGiveCcy}
              clGetAmt={order.clGetAmt}
              clGetCcy={order.clGetCcy}
              exGetAmt={order.exGetAmt}
              exGetCcy={order.exGetCcy}
              receiveUsdt={receiveUsdt}
              coefUsdt={coefUsdt}
              payUsdt={payUsdt}
              fixUsdt={order.fixUsdt}
              bonusUsdt={order.bonusUsdt}
              profitUsdt={profitUsdt}
              giveRate={giveRate}
              payRate={payRate}
              isLoss={isLoss}
              isNearZero={profitUsdt.abs().lt('0.005')}
              doneSteps={doneSteps}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Курс из снапшота заявки с inline-уточнением («узнали фактический фикс
 * конвертации позже»). PATCH /orders/:id/profit-rates пересчитывает профит
 * server-of-truth'ом и пишет изменение в аудит-журнал заявки.
 */
function RateFix({
  orderId,
  ccy,
  value,
  editable,
}: {
  orderId: string;
  ccy: CcyCode;
  value: Money;
  editable: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateProfitRates(orderId);

  if (!editable) return <span>{value}</span>;

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setError(null);
          setEditing(true);
        }}
        title={`Уточнить фактический фикс курса ${ccy} (изменение попадёт в аудит)`}
        className="rounded border-b border-dashed border-border-strong px-0.5 text-text transition-colors duration-150 hover:border-accent hover:text-accent"
      >
        {value} ✎
      </button>
    );
  }

  async function save() {
    const raw = draft.trim();
    if (!/^\d+(\.\d+)?$/.test(raw) || D(raw).lte(0)) {
      setError('курс > 0');
      return;
    }
    try {
      await update.mutateAsync({ [ccy]: D(raw).toDecimalPlaces(4).toString() });
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(',', '.'))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-6 w-24 rounded border border-[var(--accent-border)] bg-bg px-1.5 font-mono text-[12px] tabular-nums text-text outline-none"
      />
      <button type="button" onClick={() => void save()} disabled={update.isPending} className="text-success" title="Сохранить (Enter)">
        ✓
      </button>
      <button type="button" onClick={() => setEditing(false)} className="text-text-subtle" title="Отмена (Esc)">
        ✕
      </button>
      {error && <span className="text-[10px] text-danger">{error}</span>}
    </span>
  );
}

interface SideProps {
  title: string;
  account: string;
  giveLabel: string;
  giveAmt: Money;
  giveCcy: CcyCode;
  getLabel: string;
  getAmt: Money;
  getCcy: CcyCode;
  ratePair: { pair: string; value: string } | null;
  /** Опциональное действие под кодом счёта (напр. «Сменить исполнителя»). */
  action?: React.ReactNode;
}

function Side({ title, account, giveLabel, giveAmt, giveCcy, getLabel, getAmt, getCcy, ratePair, action }: SideProps) {
  return (
    <div className="rounded-md border border-border-subtle bg-bg p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-text2">{title}</div>
      <div className="mt-1 font-mono text-sm font-semibold text-accent">{account}</div>
      {action}
      <div className="mt-3 space-y-1.5 text-sm">
        <FlowLine direction="in" label={giveLabel} amt={giveAmt} ccy={giveCcy} />
        <FlowLine direction="out" label={getLabel} amt={getAmt} ccy={getCcy} />
        {ratePair && (
          <div className="flex items-center justify-between border-t border-border-subtle pt-1.5 text-xs">
            <span className="text-text2">
              курс{' '}
              <span className="rounded-full border border-border-strong bg-bg3 px-1.5 py-px font-mono text-[9px] font-bold text-text2">
                {ratePair.pair}
              </span>
            </span>
            <span className="font-mono font-semibold tabular-nums text-text">{ratePair.value}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function FlowLine({
  direction,
  label,
  amt,
  ccy,
}: {
  direction: 'in' | 'out';
  label: string;
  amt: Money;
  ccy: CcyCode;
}) {
  const isIn = direction === 'in';
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-text2">
        <DirectionIcon direction={direction} />
        {label}
      </span>
      <span
        className={`font-mono font-semibold tabular-nums ${isIn ? 'text-success' : 'text-danger'}`}
      >
        {fmtAmt(amt, ccy)}
      </span>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex items-center justify-center md:py-12">
      <div className="hidden h-px w-12 bg-border md:block" />
      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-bg text-text2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 12h8M12 8l4 4-4 4" />
        </svg>
      </div>
      <div className="hidden h-px w-12 bg-border md:block" />
    </div>
  );
}

function Stat({
  label,
  hint,
  children,
}: {
  label: string;
  /** ƒ-иконка разбора арифметики (FormulaHint). */
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="text-right">
      <div className="flex items-center justify-end gap-1 text-[10px] font-medium uppercase tracking-wide text-text2">
        {label}
        {hint}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function DirectionIcon({ direction }: { direction: 'in' | 'out' }) {
  // Пришло нам — стрелка вниз (на наш баланс), зелёная
  // Ушло от нас — стрелка вверх, нейтральная (или красная если loss-context)
  if (direction === 'in') {
    return (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="text-success"
      >
        <path d="M12 5v14M19 12l-7 7-7-7" />
      </svg>
    );
  }
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-danger"
    >
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

// ============================================================================
// 3. Legs — 4 ноги-карточки с obligation + reverse pair
// ============================================================================

interface LegsProps {
  order: OrderDetail;
  canConfirm: boolean;
  canEdit: boolean;
  canRollback: boolean;
  editingIdx: number | null;
  setEditingIdx: (idx: number | null) => void;
  onChanged: () => void;
}

function Legs({ order, canConfirm, canEdit, canRollback, editingIdx, setEditingIdx, onChanged }: LegsProps) {
  const reverseByIdx = useMemo(() => {
    const m = new Map<number, ReverseEntry>();
    for (const r of order.reverseEntries) m.set(r.provIndex, r);
    return m;
  }, [order.reverseEntries]);

  function renderLeg(p: Provodka) {
    const reverse = reverseByIdx.get(p.idx);
    const editable = order.statusFlag === 'active' && p.status === 'pending' && canEdit;
    const isEditing = editingIdx === p.idx && editable;
    return (
      <Leg
        key={p.id}
        orderId={order.id}
        statusFlag={order.statusFlag}
        provodka={p}
        reverse={reverse}
        editable={editable}
        isEditing={isEditing}
        canConfirm={canConfirm && order.statusFlag === 'active' && p.status === 'pending'}
        canRollback={canRollback && p.status === 'done'}
        onStartEdit={() => setEditingIdx(p.idx)}
        onCancelEdit={() => setEditingIdx(null)}
        onChanged={onChanged}
      />
    );
  }

  // Две колонки зеркально форме создания: клиентская нога сделки (idx 0,1)
  // и нога исполнителя (idx 2,3).
  const clientLegs = order.provodki.filter((p) => p.idx <= 1);
  const executorLegs = order.provodki.filter((p) => p.idx >= 2);

  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text2">
        Проводки сделки
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div className="mb-2 flex items-baseline gap-2 text-[11px]">
            <span className="font-medium uppercase tracking-wide text-text2">Клиент</span>
            <span className="font-mono text-accent">{order.client}</span>
          </div>
          <div className="space-y-2">{clientLegs.map(renderLeg)}</div>
        </div>
        <div>
          <div className="mb-2 flex items-baseline gap-2 text-[11px]">
            <span className="font-medium uppercase tracking-wide text-text2">Исполнитель</span>
            <span className="font-mono text-accent">{order.executor}</span>
          </div>
          <div className="space-y-2">{executorLegs.map(renderLeg)}</div>
        </div>
      </div>
    </div>
  );
}

interface LegProps {
  orderId: string;
  statusFlag: 'active' | 'reject' | 'deleted';
  provodka: Provodka;
  reverse?: ReverseEntry;
  editable: boolean;
  isEditing: boolean;
  canConfirm: boolean;
  canRollback: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChanged: () => void;
}

function Leg({
  orderId,
  statusFlag,
  provodka: p,
  reverse,
  editable,
  isEditing,
  canConfirm,
  canRollback,
  onStartEdit,
  onCancelEdit,
  onChanged,
}: LegProps) {
  const confirm = useConfirmProvodka(orderId);
  const edit = useEditProvodka(orderId);
  const rollback = useRollbackConfirm(orderId);

  const isNeg = D(p.amount).isNegative();
  const direction: 'in' | 'out' = isNeg ? 'in' : 'out';
  const absAmt = D(p.amount).abs().toString();
  const isDone = p.status === 'done';
  const isRejected = statusFlag === 'reject';

  // Описание ноги: «{role} → нам {amt}» или «мы → {role} {amt}»
  const description = isNeg
    ? `${p.accountLabel} → нам`
    : `Мы → ${p.accountLabel.toLowerCase()}`;

  const stateColor = isDone
    ? 'border-[var(--green-border)] bg-[var(--green-bg)]/50'
    : isRejected
      ? 'border-border bg-bg2 opacity-60'
      : 'border-border bg-bg2';

  return (
    <div className={`overflow-hidden rounded-lg border ${stateColor}`}>
      {/* Header ноги: жёсткая раскладка без flex-wrap — в узкой колонке
          длинные имена счетов обрезаются, а не ломают строку вертикально. */}
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${
            isDone ? 'border-border-strong bg-bg3 text-text-subtle' : 'border-border bg-bg text-text2'
          }`}
        >
          {p.idx + 1}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="shrink-0 self-center"><DirectionIcon direction={direction} /></span>
            <span className={`truncate text-sm font-medium ${isDone ? 'text-text2' : 'text-text'}`}>{description}</span>
            <span className={`shrink-0 font-mono text-sm font-bold tabular-nums ${isDone ? 'text-text2' : 'text-text'}`}>
              {fmtAmt(absAmt, p.ccy)}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[11px] text-text2">
            <span className="uppercase tracking-wide">{p.accountLabel}</span>{' '}
            <span className="font-mono">{p.account}</span>
          </div>
        </div>
        {!isEditing && (
          <div className="flex shrink-0 items-center gap-1.5">
            {canConfirm && (
              <button
                onClick={async () => {
                  await confirm.mutateAsync(p.idx);
                  onChanged();
                }}
                disabled={confirm.isPending}
                title={p.actionLabel}
                className={btnSuccessSm}
              >
                Подтвердить
              </button>
            )}
            {editable && (
              <button
                onClick={onStartEdit}
                aria-label="Редактировать"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-bg text-text2 transition-colors duration-150 hover:bg-bg2 hover:text-text"
              >
                ✎
              </button>
            )}
            {isDone && (
              <>
                {canRollback && (
                  <button
                    onClick={async () => {
                      await rollback.mutateAsync(p.idx);
                      onChanged();
                    }}
                    disabled={rollback.isPending}
                    title="Откатить подтверждение — нога вернётся в «ожидает», обязательство снова откроется"
                    className="inline-flex h-7 items-center justify-center rounded-md border border-[var(--red-border)] bg-bg px-2 text-xs font-medium text-danger transition-colors duration-150 hover:bg-[var(--red-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Откатить
                  </button>
                )}
                <DoneTag />
              </>
            )}
          </div>
        )}
      </div>

      {/* Edit row или детали проводки/реверса */}
      {isEditing ? (
        <EditPanel
          provodka={p}
          isPending={edit.isPending}
          onCancel={onCancelEdit}
          onSave={async (patch) => {
            await edit.mutateAsync({ idx: p.idx, patch });
            onCancelEdit();
            onChanged();
          }}
        />
      ) : (
        <LegDetails provodka={p} reverse={reverse} />
      )}
    </div>
  );
}

function DoneTag() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-1 text-[11px] font-semibold text-success">
      <CheckIcon /> Готово
    </span>
  );
}

function CheckIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LegDetails({ provodka: p, reverse }: { provodka: Provodka; reverse?: ReverseEntry }) {
  return (
    <div className="border-t border-border-subtle bg-bg/50 px-3 py-2 text-[11px]">
      <div className="grid grid-cols-1 gap-1.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-text2">{p.id}</span>
            <span className="text-text2">обязательство</span>
          </div>
          {/* Sign-модель: amt<0 = «нам должны» = приход (зелёный); amt>0 = «мы должны» = расход (красный). */}
          <span
            className={`font-mono font-semibold tabular-nums ${
              D(p.amount).isNegative() ? 'text-success' : 'text-danger'
            }`}
          >
            {fmtAmt(p.amount, p.ccy)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          {reverse ? (
            <>
              <div className="flex items-center gap-2">
                <span className="font-mono text-text2">{reverse.id}</span>
                <span className="text-text2">
                  исполнено{' '}
                  {p.confirmedAt ? <span className="font-mono">· {formatDateTime(p.confirmedAt)}</span> : null}
                </span>
              </div>
              {/* Reverse имеет противоположный obligation знак: amt>0 = реальный приход, amt<0 = реальный расход. */}
              <span
                className={`font-mono font-semibold tabular-nums ${
                  D(reverse.amount).isNegative() ? 'text-danger' : 'text-success'
                }`}
              >
                {fmtAmt(reverse.amount, reverse.ccy)}
              </span>
            </>
          ) : (
            <span className="text-text-subtle">ожидает подтверждения</span>
          )}
        </div>
      </div>
    </div>
  );
}

function EditPanel({
  provodka: p,
  isPending,
  onCancel,
  onSave,
}: {
  provodka: Provodka;
  isPending: boolean;
  onCancel: () => void;
  onSave: (patch: EditProvodkaInput) => Promise<void>;
}) {
  const [account, setAccount] = useState(p.account);
  const [ccy, setCcy] = useState<CcyCode>(p.ccy);
  const [amount, setAmount] = useState(p.amount);
  const [amountError, setAmountError] = useState<string | null>(null);

  return (
    <div className="border-t border-border-subtle bg-bg3 px-4 py-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr_auto]">
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text2">Счёт</label>
          <input
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text2">Валюта</label>
          <div className="w-24">
            <Combobox
              value={ccy}
              onChange={(val) => setCcy(val as CcyCode)}
              options={CCY_CODES.map((c) => ({ value: c, label: c }))}
              searchable={false}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text2">Сумма</label>
          <input
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountError(null);
            }}
            className={`${inputCls} text-right font-mono tabular-nums`}
          />
          {amountError && <div className="mt-1 text-[11px] text-danger">{amountError}</div>}
        </div>
        <div className="flex items-end gap-1.5">
          <button
            disabled={isPending}
            onClick={() => {
              // Нормализуем сумму до отправки: пробелы/запятая → decimal,
              // квантуем до точности хранения. Иначе '1 000,50' падает
              // непрозрачной 400 от MoneyStr-regex на сервере.
              let amountPatch: string | undefined;
              if (amount !== p.amount) {
                const raw = amount.trim().replace(/\s+/g, '').replace(',', '.');
                if (!/^-?\d+(\.\d+)?$/.test(raw)) {
                  setAmountError('Сумма должна быть числом (знак минус допустим)');
                  return;
                }
                amountPatch = D(raw).toDecimalPlaces(MONEY_SCALE).toString();
                if (amountPatch === p.amount) amountPatch = undefined;
              }
              setAmountError(null);
              void onSave({
                account: account !== p.account ? account : undefined,
                ccy: ccy !== p.ccy ? ccy : undefined,
                amount: amountPatch,
              });
            }}
            className={btnSuccessSm}
          >
            Сохранить
          </button>
          <button onClick={onCancel} className={btnGhostSm}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 4. Журнал движения — collapsible сырой список TX
// ============================================================================

function JournalToggle({
  order,
  expanded,
  onToggle,
}: {
  order: OrderDetail;
  expanded: boolean;
  onToggle: () => void;
}) {
  const totalTx = order.provodki.length + order.reverseEntries.length;
  return (
    <div className="rounded-lg border border-border bg-bg2">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-bg3"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`text-text2 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="text-sm font-medium text-text">Журнал движения</span>
          <span className="text-xs text-text2">
            · {order.provodki.length} обязательств + {order.reverseEntries.length} исполнений = {totalTx} записей
          </span>
        </div>
        <span className="text-xs text-text2">{expanded ? 'свернуть' : 'развернуть'}</span>
      </button>
      {expanded && (
        <div className="border-t border-border-subtle">
          <JournalTable order={order} />
        </div>
      )}
    </div>
  );
}

function JournalTable({ order }: { order: OrderDetail }) {
  const rows = useMemo(() => {
    const all: Array<{ id: string; type: string; account: string; accountLabel: string; ccy: CcyCode; amount: Money; date: string }> = [];
    for (const p of order.provodki) {
      all.push({
        id: p.id,
        type: 'обязательство',
        account: p.account,
        accountLabel: p.accountLabel,
        ccy: p.ccy,
        amount: p.amount,
        date: p.date,
      });
    }
    for (const r of order.reverseEntries) {
      all.push({
        id: r.id,
        type: 'исполнение',
        account: r.account,
        accountLabel: r.accountLabel,
        ccy: r.ccy,
        amount: r.amount,
        date: r.date,
      });
    }
    return all.sort((a, b) => a.id.localeCompare(b.id));
  }, [order]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-bg3 text-text2">
            <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide">ID</th>
            <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide">Тип</th>
            <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide">Счёт</th>
            <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide">Сумма</th>
            <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide">Дата</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isNeg = D(r.amount).isNegative();
            return (
              <tr key={r.id} className="border-b border-border-subtle last:border-b-0">
                <td className="px-3 py-2.5 font-mono text-xs text-text2">{r.id}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${
                      r.type === 'обязательство'
                        ? 'border-border bg-bg3 text-text2'
                        : 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
                    }`}
                  >
                    {r.type}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wide text-text2">{r.accountLabel}</div>
                  <div className="font-mono text-sm text-text">{r.account}</div>
                </td>
                <td
                  className={`px-3 py-2.5 text-right font-mono font-semibold tabular-nums ${
                    isNeg ? 'text-danger' : 'text-success'
                  }`}
                >
                  {fmtAmt(r.amount, r.ccy)}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-text2">{r.date}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// 5. Контекстный footer — primary action справа
// ============================================================================

function ContextActions({
  order,
  onClose,
  canDelete,
  onDelete,
}: {
  order: OrderDetail;
  onClose: () => void;
  canDelete: boolean;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle pt-4">
      {canDelete && order.status !== 'deleted' && (
        <button
          onClick={onDelete}
          className="mr-auto rounded-md border border-[var(--red-border)] bg-bg px-3 py-1.5 text-sm font-medium text-danger transition-colors duration-150 hover:bg-[var(--red-bg)]"
        >
          Удалить заявку
        </button>
      )}
      <div className="flex gap-2">
        <button onClick={onClose} className={btnSecondary}>
          Закрыть
        </button>
        {order.status === 'progress' || order.status === 'new' ? (
          <NextLegHint order={order} />
        ) : null}
      </div>
    </div>
  );
}

function NextLegHint({ order }: { order: OrderDetail }) {
  // Подсказка про следующую ногу — primary-кнопка кликабельная только если она в пределах
  const next = order.provodki.find((p) => p.status === 'pending');
  if (!next) return null;
  // Кликает по «✎» в leg-карточке — здесь же мы только подсказываем визуально
  return (
    <div className="hidden items-center text-xs text-text2 sm:flex">
      <span>↑ подтвердите проводку </span>
      <span className="ml-1 font-semibold text-accent">№ {next.idx + 1}</span>
    </div>
  );
}
